/**
 * Report conversion FLOW registry — the Orca-owned identity source for
 * variants A–D.
 *
 * Extracted out of `app/src/contexts/ReportConversionFlowContext.jsx` (which
 * now re-exports every name below unchanged) for one reason: the registry is a
 * cross-repository CONTRACT, and a contract that only exists inside a `.jsx`
 * React module cannot be read by a Node generator without either parsing JSX or
 * re-typing the values. Both of those are drift factories. This module is plain
 * ESM with no React import, so `scripts/build-report-flow-registry.js` imports
 * the exact same objects the running app uses — the published artifact is a
 * projection of this file, never a second copy of it.
 *
 * FLOW is a separate dimension from Message: any of the 6 Message destinations
 * can be shown through any of these flows (6 × 4 = 24 measurement arms).
 *
 * Variant D (`purchase_first_flow`) remains the public DEFAULT — a landing
 * page with no flow selector, or an invalid/unapproved one, always fails
 * safe to D. As of 2026-08-17 (Derek), Variants A/B/C are owner-approved as
 * real, paid-eligible, publicly-reachable flows too: `?flow_key=...&flow_version=...`
 * on a public `/report*` URL selects a non-default flow when the pair is
 * registered AND `paidCampaignEligible` — see `resolvePublicFlowFromSearchParams`.
 * Its implementation key names the behavior rather than the baseline ADU
 * Standard Message.
 */

export const REPORT_CONVERSION_FLOWS = Object.freeze({
    DIRECT_CHECKOUT: 'direct_checkout_flow',
    LEAD_FIRST: 'lead_first_flow',
    WELCOME_GATE: 'welcome_gate_flow',
    PURCHASE_FIRST: 'purchase_first_flow',
});

// Single flow-registry version. Bump when a flow's behavior/eligibility changes in
// a way that must invalidate prior measurement joins.
export const REPORT_FLOW_VERSION = 'v1';

// Orca-owned flow registry — the source of truth Dash reads, never redefines.
// Fields per the v3 reconciliation: flowKey, displayName, flowVersion, isDefault,
// internalPreviewOnly, ctaHandoff, paidCampaignEligible.
//   • paidCampaignEligible: null = UNRESOLVED (not approved); true = approved for
//     real paid-campaign traffic. All four flows were explicitly approved
//     2026-08-17 (Derek) so all 6 messages × 4 flows can be measured under real
//     ad traffic. A flow being eligible does not itself activate any campaign —
//     campaign activation stays a separate, owner-gated Meta Ads decision.
//   • capturesReportLead: whether this flow may mint a report_leads record.
//     Mirrors LEAD_ELIGIBLE_FLOW_KEYS in functions/reportLeads.js, which is the
//     enforcing boundary — this field is the published DESCRIPTION of that
//     boundary, and build-report-flow-registry.js fails closed if the two
//     disagree. B never captures a lead by product contract; D never runs the
//     qualifier at all.
//   • leadMarketingConsent: how marketingConsent is determined on the flows that
//     do capture. 'user_opt_in' = the buyer's own unchecked-by-default choice.
//     'never' = service-only recovery, forced false server-side regardless of
//     what any client sends (owner decision for Variant C).
export const REPORT_FLOW_REGISTRY = Object.freeze({
    [REPORT_CONVERSION_FLOWS.DIRECT_CHECKOUT]: Object.freeze({
        flowKey: REPORT_CONVERSION_FLOWS.DIRECT_CHECKOUT,
        displayName: 'Direct Checkout',
        flowVersion: REPORT_FLOW_VERSION,
        isDefault: false,
        internalPreviewOnly: false,
        ctaHandoff: 'zip_run_check_preview_then_checkout',
        paidCampaignEligible: true,
        capturesReportLead: false,
        leadMarketingConsent: null,
    }),
    [REPORT_CONVERSION_FLOWS.LEAD_FIRST]: Object.freeze({
        flowKey: REPORT_CONVERSION_FLOWS.LEAD_FIRST,
        displayName: 'Lead First',
        flowVersion: REPORT_FLOW_VERSION,
        isDefault: false,
        internalPreviewOnly: false,
        ctaHandoff: 'zip_run_check_email_reveal_then_checkout',
        paidCampaignEligible: true,
        capturesReportLead: true,
        leadMarketingConsent: 'user_opt_in',
    }),
    [REPORT_CONVERSION_FLOWS.WELCOME_GATE]: Object.freeze({
        flowKey: REPORT_CONVERSION_FLOWS.WELCOME_GATE,
        displayName: 'Welcome Gate',
        flowVersion: REPORT_FLOW_VERSION,
        isDefault: false,
        internalPreviewOnly: false,
        ctaHandoff: 'zip_run_check_then_welcome_gate_then_checkout',
        paidCampaignEligible: true,
        capturesReportLead: true,
        leadMarketingConsent: 'never',
    }),
    [REPORT_CONVERSION_FLOWS.PURCHASE_FIRST]: Object.freeze({
        flowKey: REPORT_CONVERSION_FLOWS.PURCHASE_FIRST,
        displayName: 'Default Standard',
        flowVersion: REPORT_FLOW_VERSION,
        isDefault: true,
        internalPreviewOnly: false,
        ctaHandoff: 'cta_get_report_then_checkout',
        paidCampaignEligible: true,
        capturesReportLead: false,
        leadMarketingConsent: null,
    }),
});

// The public default: a landing page with no flow selector (or an invalid/
// unapproved one) always fails closed to Variant D. A/B/C are now legitimate
// public campaign destinations, but only via an explicit, registered,
// paid-eligible `?flow_key=...&flow_version=...` pair — see
// `resolvePublicFlowFromSearchParams` below.
export const DEFAULT_REPORT_CONVERSION_FLOW = REPORT_CONVERSION_FLOWS.PURCHASE_FIRST;

// Any registered flow may be previewed from the staging/dev template rail; unknown
// values (and everything in production) fail closed to the default via setFlow.
export function isSelectableFlow(flow) {
    return Object.prototype.hasOwnProperty.call(REPORT_FLOW_REGISTRY, flow);
}

export function resolveFlowMeta(flow) {
    return REPORT_FLOW_REGISTRY[flow] || REPORT_FLOW_REGISTRY[DEFAULT_REPORT_CONVERSION_FLOW];
}

// Validate a (key, version) pair against the registry ATOMICALLY: the pair is
// returned only if the key is registered AND the version matches that flow's
// registered version; otherwise BOTH fields come back undefined. Never
// sanitizes into new values, never returns a half-pair. This is the client
// mirror of functions/conversions/reportConversionFlows.js and is used to
// validate untrusted URL handoff params before they can override attribution.
export function resolveRegisteredFlowIdentity(rawFlowKey, rawFlowVersion) {
    const flowKey = typeof rawFlowKey === 'string' ? rawFlowKey.trim() : '';
    const flowVersion = typeof rawFlowVersion === 'string' ? rawFlowVersion.trim() : '';
    const meta = REPORT_FLOW_REGISTRY[flowKey];
    if (!meta || meta.flowVersion !== flowVersion) {
        return { flowKey: undefined, flowVersion: undefined };
    }
    return { flowKey, flowVersion };
}

// Public campaign identity is stricter than registry membership: the pair must
// be exact, explicitly paid-eligible, and no longer marked as preview-only.
// Returning an empty pair lets callers preserve only an applied public identity
// without silently translating rejected input into a different named flow.
export function resolvePublicFlowIdentity(rawFlowKey, rawFlowVersion) {
    const identity = resolveRegisteredFlowIdentity(rawFlowKey, rawFlowVersion);
    const meta = identity.flowKey ? REPORT_FLOW_REGISTRY[identity.flowKey] : null;
    if (
        !meta
        || meta.paidCampaignEligible !== true
        || meta.internalPreviewOnly !== false
    ) {
        return { flowKey: undefined, flowVersion: undefined };
    }
    return identity;
}

// Public entry point for real (paid) traffic: reads `flow_key`/`flow_version`
// off a landing URL's query string and resolves the requested flow ONLY when
// it is a registered pair (via resolveRegisteredFlowIdentity above) AND
// explicitly paidCampaignEligible. Anything else — no param, an unknown key,
// a mismatched version, or a registered-but-ineligible flow — fails safe to
// DEFAULT_REPORT_CONVERSION_FLOW (Variant D). This is the only place flow
// selection is public; ReportConversionFlowContext's setFlow stays gated to
// isStagingOrDev for internal QA toggling after mount.
export function resolvePublicFlowFromSearchParams(search) {
    const params = new URLSearchParams(search || '');
    const { flowKey } = resolvePublicFlowIdentity(
        params.get('flow_key'),
        params.get('flow_version'),
    );
    return flowKey || DEFAULT_REPORT_CONVERSION_FLOW;
}

// The flows that may mint a report_leads record, derived from the registry
// rather than re-listed. Mirrors LEAD_ELIGIBLE_FLOW_KEYS in
// functions/reportLeads.js (the enforcing boundary).
export const LEAD_CAPTURE_FLOW_KEYS = Object.freeze(
    Object.values(REPORT_FLOW_REGISTRY)
        .filter((meta) => meta.capturesReportLead)
        .map((meta) => meta.flowKey)
        .sort(),
);

export function flowCapturesReportLead(flowKey) {
    return resolveFlowMeta(flowKey).capturesReportLead === true;
}
